/**
 * Tiny key-value stores used by the offline layer. Same async API:
 *   put(table, key, value) / get / getAll / remove
 * `createIdbStore` persists in IndexedDB (browser); `createMemoryStore` is
 * used in tests and as a fallback when IndexedDB is unavailable.
 */
export function createMemoryStore() {
  const tables = {};
  const t = (n) => (tables[n] = tables[n] || new Map());
  return {
    async put(table, key, value) { t(table).set(key, value); },
    async get(table, key) { return t(table).get(key); },
    async getAll(table) { return [...t(table).values()]; },
    async remove(table, key) { t(table).delete(key); },
  };
}

const TABLES = ["outbox", "cache", "meta"];

export function createIdbStore(name = "phoenix-offline", tables = TABLES) {
  const open = () => new Promise((resolve, reject) => {
    const req = indexedDB.open(name, 1);
    req.onupgradeneeded = () => tables.forEach((n) => req.result.createObjectStore(n));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  let dbp = null;
  const run = async (table, mode, fn) => {
    dbp = dbp || open();
    const db = await dbp;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(table, mode);
      const req = fn(tx.objectStore(table));
      tx.oncomplete = () => resolve(req && req.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  };
  return {
    put: (table, key, value) => run(table, "readwrite", (s) => s.put(value, key)),
    get: (table, key) => run(table, "readonly", (s) => s.get(key)),
    getAll: (table) => run(table, "readonly", (s) => s.getAll()),
    remove: (table, key) => run(table, "readwrite", (s) => s.delete(key)),
  };
}

export function createDefaultStore() {
  return typeof indexedDB !== "undefined" ? createIdbStore() : createMemoryStore();
}
